import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { Loader } from '@/components/ui/Loader'
import { PdfViewer, type PdfViewerState } from '@/components/partner/PdfViewer'
import { StatusBadge } from '@/components/inspections/LeadBadges'
import {
  getPartnerLead,
  getPartnerMe,
  partnerAutotekaUrl,
  partnerCmExpertUrl,
  partnerProtocolUrl,
  partnerStatuses,
  partnerStatusLabelKey,
  type PartnerFileUrls,
  type PartnerLead,
} from '@/api/partner'
import type { InspectionLeadStatus, LeadPipeline } from '@/types/inspection-lead'
import { formatMoscowDateTime, formatRub } from '@/utils/format'
import { formatLicensePlate } from '@/utils/licensePlate'
import { formatPhoneDisplay } from '@/utils/phone'
import i18n from '@/utils/i18n'

const card = 'rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-5'
const cardTitle =
  'mb-4 font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold text-[color:var(--stryzh-color-text-primary)]'
const muted = 'text-[color:var(--stryzh-color-text-secondary)]'
const link = 'text-[color:var(--stryzh-color-accent-orange)] hover:underline'

/** /partner/leads/:id — заявка глазами партнёра: этапы с датами, документы, данные. */
export function PartnerLeadPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  // partner_me уже загружен каркасом (PartnerShell) — берём из кэша.
  const me = useQuery({ queryKey: ['partner-me'], queryFn: getPartnerMe, staleTime: 5 * 60_000 })
  const q = useQuery({
    queryKey: ['partner-lead', id],
    queryFn: () => getPartnerLead(id),
    refetchInterval: 60_000,
  })

  if (q.isLoading) return <Loader centered className="py-16" />
  if (!q.data) {
    return (
      <div>
        <Breadcrumbs items={[{ label: t('partner.title'), to: '/partner' }]} />
        <div className={muted}>{q.error ? t('partner.lead.notFound') : null}</div>
      </div>
    )
  }

  const lead = q.data
  const pipeline: LeadPipeline = me.data?.pipeline ?? 'sberauto'
  const vehicle = [lead.vehicle_brand, lead.vehicle_model, lead.vehicle_year].filter(Boolean).join(' ')
  const title = vehicle || t('partner.lead.no', { no: lead.lead_no })

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: t('partner.title'), to: '/partner' },
          { label: t('partner.lead.no', { no: lead.lead_no }) },
        ]}
      />
      <header className="mb-6 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="min-w-0 break-words font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-hero)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
            {title}
          </h1>
          <StatusBadge status={lead.status} label={t(partnerStatusLabelKey(lead.status, pipeline))} />
        </div>
        <div className={clsx('text-sm', muted)}>
          {[
            t('partner.lead.no', { no: lead.lead_no }),
            lead.external_id ? t('partner.lead.crm', { id: lead.external_id }) : null,
            lead.license_plate ? formatLicensePlate(lead.license_plate) : null,
            t('partner.lead.created', { at: formatMoscowDateTime(lead.created_at) }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </header>

      {/* Две колонки и два ряда: «Данные» с «Историей», «Документы» с
          комментарием. Ряд задаёт высоту обеим карточкам (решение Sage 05.11:
          выровнять по высоте). На узком экране порядок: данные, документы,
          история, комментарий. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="lg:col-start-1 lg:row-start-1">
          <LeadFacts lead={lead} className="h-full" />
        </div>
        <div className="lg:col-start-1 lg:row-start-2">
          <LeadDocuments lead={lead} className="h-full" />
        </div>
        <div className="lg:col-start-2 lg:row-start-1">
          <section className={clsx(card, 'h-full')}>
            <h2 className={cardTitle}>{t('partner.lead.progress')}</h2>
            <LeadProgress lead={lead} pipeline={pipeline} />
          </section>
        </div>
        <div className="lg:col-start-2 lg:row-start-2">
          {lead.partner_comment && lead.status !== 'auction' ? (
            // Комментарий Carlink AI о текущем состоянии: рядом с историей
            // этапов, а не среди статичных данных заявки (решение Sage 05.11).
            // На торгах комментарий не нужен (решение Sage 05.11): итог и
            // ставки партнёр видит в истории.
            <section className={clsx(card, 'h-full')}>
              <h2 className={cardTitle}>
                {t('partner.lead.f.partnerComment')}
                {lead.partner_comment_at ? (
                  <span className={clsx('ml-2 text-xs font-normal', muted)}>
                    {t('partner.lead.commentUpdated', { at: formatMoscowDateTime(lead.partner_comment_at) })}
                  </span>
                ) : null}
              </h2>
              <p className="whitespace-pre-wrap break-words text-sm text-[color:var(--stryzh-color-text-primary)]">
                {lead.partner_comment}
              </p>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** Конечные статусы вне основной линии этапов: лид закрыт, но не «Продажа состоялась». */
const CLOSING_STATUSES: InspectionLeadStatus[] = ['not_sold', 'cancelled']

/** Комментарий диспетчера как есть, только с заглавной буквы. */
function capitalize(text: string): string {
  const v = text.trim()
  return v ? v[0].toUpperCase() + v.slice(1) : v
}

interface Step {
  status: InspectionLeadStatus
  at: string | null
  state: 'done' | 'current' | 'todo' | 'cancelled'
  notes: ReactNode[]
}

/** Этапы воронки сверху вниз. Пройденные — с датой входа; отменённая заявка
 *  заканчивается шагом «Отменён» с причиной, непроданная — шагом «Продажа не
 *  состоялась» с итогом торгов. */
function LeadProgress({ lead, pipeline }: { lead: PartnerLead; pipeline: LeadPipeline }) {
  const { t } = useTranslation()
  const reachedAt = new Map<InspectionLeadStatus, string>()
  for (const h of lead.history) if (!reachedAt.has(h.status)) reachedAt.set(h.status, h.at)
  // Даты торгов — из лота: журнал знает только момент, когда мост увидел лот
  // (подключённый позже мост перескакивает этапы и ставит свои даты).
  const lot = lead.lot
  if (lot?.started_at && lot.status && !['DRAFT', 'MODERATED'].includes(lot.status)) {
    reachedAt.set('auction', lot.started_at)
  }
  if (lot?.finished_at) reachedAt.set('approval', lot.finished_at)

  const order = partnerStatuses(pipeline).filter((s) => !CLOSING_STATUSES.includes(s))
  // Итог торгов: в «Согласование» лид попадает, когда торги кончились (FINISHED
  // и финальные статусы); is_final у FINISHED ещё false, но итог уже известен.
  const auctionNotes = (): ReactNode[] => {
    const out: ReactNode[] = []
    if (!lead.lot?.status) return out
    const key = `partner.lead.auctionResult.${lead.lot.status}`
    if (i18n.exists(key)) out.push(t(key))
    if (lead.lot.leader_summa != null) {
      out.push(
        t('partner.lead.finalBid', {
          price: formatRub(lead.lot.leader_summa),
          leader: lead.lot.leader_company ?? t('partner.lead.leaderPerson'),
        }),
      )
    } else if (lead.lot.start_price != null) {
      out.push(t('partner.lead.noBids'))
    }
    return out
  }
  // Под каждым этапом свои события (решение Sage 05.11): не уведомления Сбера,
  // а то, что логично для этапа: взят в работу, контакт, осмотр проведён,
  // состояние лота, ход торгов, итог.
  const notes = (s: InspectionLeadStatus): ReactNode[] => {
    const out: ReactNode[] = []
    const lotStatus = lead.lot?.status ?? null
    if (s === 'new' && reachedAt.has('in_progress')) {
      out.push(t('partner.lead.taken', { at: formatMoscowDateTime(reachedAt.get('in_progress')!) }))
    }
    // Детали звонков (результат, перезвон) партнёр читает в комментарии
    // Carlink AI; на этапе только факт и дата контакта.
    if (s === 'in_progress') {
      if (lead.contacted_at) out.push(t('partner.lead.contacted', { at: formatMoscowDateTime(lead.contacted_at) }))
      else if (lead.status === 'in_progress') out.push(t('partner.lead.contacting'))
    }
    if (s === 'order_created' && lead.inspection_at) {
      out.push(t('partner.lead.inspectionScheduled', { at: formatMoscowDateTime(lead.inspection_at) }))
    } else if (s === 'order_created' && lead.status === 'order_created' && !lead.inspection_failed_at) {
      out.push(t('partner.lead.inspectionPending'))
    }
    // Причину несостоявшегося осмотра партнёр читает в комментарии Carlink AI;
    // на этапе только факт и дата, обычным цветом.
    if (s === 'order_created' && lead.inspection_failed_at) {
      out.push(t('partner.lead.inspectionFailed', { at: formatMoscowDateTime(lead.inspection_failed_at) }))
    }
    // Заявка отменена с «Осмотра», время так и не назначили: иначе этап
    // «Осмотр» без подписи читается как состоявшийся осмотр.
    if (s === 'order_created' && lead.status === 'cancelled' && !lead.inspection_at && !lead.inspection_failed_at && !reachedAt.has('lot_ready')) {
      out.push(t('partner.lead.inspectionNotScheduled'))
    }
    // Осмотр проведён: акт есть (report_url), дата = выход в «Готов к торгам».
    if (s === 'order_created' && lead.report_url && !lead.inspection_failed_at && reachedAt.has('lot_ready')) {
      out.push(t('partner.lead.inspected', { at: formatMoscowDateTime(reachedAt.get('lot_ready')!) }))
    }
    if (s === 'lot_ready') {
      if (lead.lot?.start_price != null) {
        out.push(t('partner.lead.guaranteedPrice', { price: formatRub(lead.lot.start_price) }))
      }
      if (lead.lot?.draft_at) out.push(t('partner.lead.lotCreated', { at: formatMoscowDateTime(lead.lot.draft_at) }))
      else if (lotStatus === 'DRAFT' || (!lead.lot && lead.status === 'lot_ready')) out.push(t('partner.lead.lotDraft'))
      if (lotStatus === 'MODERATED') out.push(t('partner.lead.lotModeration'))
    }
    if (s === 'auction' && lead.lot?.started_at) {
      out.push(t('partner.lead.auctionStarted', { at: formatMoscowDateTime(lead.lot.started_at) }))
      if (lead.status === 'auction' && lead.lot.leader_summa != null) {
        out.push(t('partner.lead.currentBid', { price: formatRub(lead.lot.leader_summa) }))
      }
      if (lead.lot.finished_at) out.push(t('partner.lead.auctionFinished', { at: formatMoscowDateTime(lead.lot.finished_at) }))
    }
    // У непроданного лида итог показан на последнем шаге.
    if (s === 'approval' && lead.status !== 'not_sold') out.push(...auctionNotes())
    // Протокол всегда под «Согласованием результатов», и у непроданной заявки тоже.
    if (s === 'approval' && lead.lot?.protocol_at) {
      out.push(t('partner.lead.protocolReady', { at: formatMoscowDateTime(lead.lot.protocol_at) }))
    }
    if (s === 'done' && lead.lot?.leader_summa != null) {
      out.push(
        t('partner.lead.finalBid', {
          price: formatRub(lead.lot.leader_summa),
          leader: lead.lot.leader_company ?? t('partner.lead.leaderPerson'),
        }),
      )
    }
    return out
  }

  let steps: Step[]
  if (CLOSING_STATUSES.includes(lead.status)) {
    // Все этапы до последнего пройденного: пропущенные (мост подключился
    // позже) тоже пройдены, просто без даты.
    // Пройденные этапы: до того, с которого заявку закрыли (запись журнала
    // перед последним закрытием). Не «самый дальний достигнутый»: мост мог
    // мельком поставить «Готов к торгам» и вернуть обратно.
    let closingIdx = -1
    for (let i = lead.history.length - 1; i >= 0; i--) {
      if (lead.history[i].status === lead.status) { closingIdx = i; break }
    }
    const fromIdx = closingIdx > 0 ? order.indexOf(lead.history[closingIdx - 1].status) : -1
    const last = fromIdx >= 0 ? fromIdx : Math.max(...order.map((s, i) => (reachedAt.has(s) ? i : -1)))
    const passed = order.slice(0, last + 1)
    steps = [
      ...passed.map((s) => ({ status: s, at: reachedAt.get(s) ?? null, state: 'done' as const, notes: notes(s) })),
      {
        status: lead.status,
        at: reachedAt.get(lead.status) ?? lead.status_changed_at,
        state: 'cancelled',
        notes: lead.status === 'not_sold' ? auctionNotes() : lead.cancel_comment ? [capitalize(lead.cancel_comment)] : [],
      },
    ]
  } else {
    const current = order.indexOf(lead.status)
    steps = order.map((s, i) => ({
      status: s,
      at: i <= current ? (reachedAt.get(s) ?? (i === current ? lead.status_changed_at : null)) : null,
      state: i < current ? 'done' : i === current ? (s === 'done' ? 'done' : 'current') : 'todo',
      notes: i <= current ? notes(s) : [],
    }))
  }

  return (
    <ol>
      {steps.map((s, i) => (
        <li key={s.status} className="flex gap-3">
          <div className="flex w-3 flex-col items-center">
            <span
              className={clsx(
                'mt-[5px] h-3 w-3 shrink-0 rounded-full border-2',
                (s.state === 'done' || s.state === 'cancelled') &&
                  'border-[color:var(--stryzh-color-accent-orange)] bg-[color:var(--stryzh-color-accent-orange)]',
                s.state === 'current' &&
                  'stryzh-step-dot-current border-[color:var(--stryzh-color-accent-orange)] bg-transparent',
                s.state === 'todo' && 'border-[color:var(--stryzh-color-border-default)] bg-transparent',
              )}
            />
            {i < steps.length - 1 ? (
              <span
                className={clsx(
                  'my-1 w-0.5 flex-1',
                  s.state === 'done' ? 'bg-[color:var(--stryzh-color-accent-orange)]' : 'bg-[color:var(--stryzh-color-border-default)]',
                )}
              />
            ) : null}
          </div>
          <div className="min-w-0 flex-1 pb-5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span
                className={clsx(
                  'text-sm',
                  s.state === 'todo'
                    ? muted
                    : s.state === 'current' || s.state === 'cancelled'
                      ? 'font-semibold text-[color:var(--stryzh-color-text-primary)]'
                      : 'text-[color:var(--stryzh-color-text-primary)]',
                )}
              >
                {/* Итог ещё не известен: последний шаг нейтральный. */}
                {s.status === 'done' && s.state === 'todo'
                  ? t('partner.lead.saleDecision')
                  : t(partnerStatusLabelKey(s.status, pipeline))}
              </span>
              {s.at ? <span className={clsx('text-xs', muted)}>{formatMoscowDateTime(s.at)}</span> : null}
            </div>
            {s.notes.map((n, j) => (
              <div key={j} className={clsx('mt-1 whitespace-pre-wrap break-words text-sm', muted)}>
                {n}
              </div>
            ))}
          </div>
        </li>
      ))}
    </ol>
  )
}

function LeadDocuments({ lead, className }: { lead: PartnerLead; className?: string }) {
  const { t } = useTranslation()
  const [fileError, setFileError] = useState<{ key: string; message: string } | null>(null)
  const [viewer, setViewer] = useState<PdfViewerState | null>(null)
  const protocol = lead.lot?.protocol_path ?? null
  const autoteka = lead.autoteka_pdf_path
  const cmExpert = lead.cm_expert_pdf_path
  const lotUrl = lead.lot?.lot_url ?? null

  // Файлы из приватных bucket'ов: подписанная ссылка на 10 минут. PDF
  // открывается в просмотрщике прямо здесь, скачивание кнопкой в нём.
  const openFile = async (key: string, title: string, getUrls: () => Promise<PartnerFileUrls>) => {
    setFileError(null)
    try {
      setViewer({ title, urls: await getUrls() })
    } catch (e) {
      setFileError({ key, message: (e as Error).message })
    }
  }
  const fileErrorFor = (key: string) =>
    fileError?.key === key ? <div className="mt-1 text-xs text-red-300">{fileError.message}</div> : null

  const hasDocs = Boolean(autoteka || cmExpert || lead.report_url || lotUrl || protocol)
  // Закрытой заявке без документов обещать «появится после…» незачем.
  if (!hasDocs && (CLOSING_STATUSES.includes(lead.status) || lead.status === 'done')) return null

  return (
    <section className={clsx(card, className)}>
      <h2 className={cardTitle}>{t('partner.lead.docs')}</h2>
      {hasDocs ? (
        <ul className="flex flex-col gap-2 text-sm">
          {autoteka ? (
            <li>
              <button
                type="button"
                onClick={() => void openFile('autoteka', t('partner.lead.autotekaTitle'), () => partnerAutotekaUrl(autoteka, lead.lead_no))}
                className={link}
              >
                {t('partner.lead.autoteka')}
              </button>
              {fileErrorFor('autoteka')}
            </li>
          ) : null}
          {cmExpert ? (
            <li>
              <button
                type="button"
                onClick={() => void openFile('cmExpert', t('partner.lead.cmExpertTitle'), () => partnerCmExpertUrl(cmExpert, lead.lead_no))}
                className={link}
              >
                {t('partner.lead.cmExpert')}
              </button>
              {lead.cm_expert_pdf_at ? (
                <span className={clsx('ml-2 text-xs', muted)}>{formatMoscowDateTime(lead.cm_expert_pdf_at)}</span>
              ) : null}
              {fileErrorFor('cmExpert')}
            </li>
          ) : null}
          {lead.report_url ? (
            <li>
              <a href={lead.report_url} target="_blank" rel="noreferrer" className={link}>
                {t('partner.lead.report')}
              </a>
            </li>
          ) : null}
          {lotUrl ? (
            <li>
              <a href={lotUrl} target="_blank" rel="noreferrer" className={link}>
                {t('partner.lead.lot')}
              </a>
            </li>
          ) : null}
          {protocol ? (
            <li>
              <button
                type="button"
                onClick={() => void openFile('protocol', t('partner.lead.protocolTitle'), () => partnerProtocolUrl(protocol, lead))}
                className={link}
              >
                {t('partner.lead.protocol')}
              </button>
              {fileErrorFor('protocol')}
            </li>
          ) : null}
        </ul>
      ) : (
        <p className={clsx('text-sm', muted)}>{t('partner.lead.noDocs')}</p>
      )}
      {viewer ? <PdfViewer title={viewer.title} urls={viewer.urls} onClose={() => setViewer(null)} /> : null}
    </section>
  )
}


function LeadFacts({ lead, className }: { lead: PartnerLead; className?: string }) {
  const { t } = useTranslation()
  const vehicle = [lead.vehicle_brand, lead.vehicle_model, lead.vehicle_year].filter(Boolean).join(' ')
  const rows: [string, string | null, boolean?][] = [
    ['client', lead.client_name],
    ['phone', lead.client_phone ? formatPhoneDisplay(lead.client_phone) : null],
    ['city', lead.location],
    ['address', lead.inspection_address],
    ['vehicle', vehicle || null],
    ['plate', lead.license_plate ? formatLicensePlate(lead.license_plate) : null, true],
    ['vin', lead.vehicle_vin, true],
    ['newCar', lead.trade_in_target_vehicle],
    ['crm', lead.external_id, true],
  ]
  return (
    <section className={clsx(card, className)}>
      <h2 className={cardTitle}>{t('partner.lead.data')}</h2>
      <dl className="flex flex-col gap-3 text-sm">
        {rows
          .filter(([, v]) => v)
          .map(([key, value, mono]) => (
            <div key={key}>
              <dt className={clsx('text-xs', muted)}>{t(`partner.lead.f.${key}`)}</dt>
              <dd
                className={clsx(
                  'whitespace-pre-wrap break-words text-[color:var(--stryzh-color-text-primary)]',
                  mono && 'font-[family-name:var(--stryzh-font-family-mono)]',
                )}
              >
                {value}
              </dd>
            </div>
          ))}
      </dl>
    </section>
  )
}
